/* Datum- en tijdvelden (DateTimeField.tsx, TimeField.tsx). De basis (rand, hoogte, focus) komt van
   TextField.module.css; hier wat de native datuminvoer nodig heeft om rustig en leesbaar te blijven
   in Chrome, Edge, Safari/WebKit en Firefox, en het eigen tijdveld (tekst + klokknop). */

/* Nooit smaller dan de waarde plus het icoon; in een smalle container wint 100%. */
.date {
  min-width: min(100%, 10.5rem);
}

.time {
  min-width: min(100%, 8.75rem);
}

/* Dubbele klasse: wint altijd van TextField.module.css .input, ongeacht de laadvolgorde. */
.input.input {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  line-height: 1.25;
  text-align: start;
  /* Verticaal gecentreerd, ook in WebKit (daar staat de waarde anders bovenin het veld): 44 px =
     2 × 1.5 px rand + 2 × 10 px + 21 px regel. Het icoon zit binnen het veld (Chromium/Firefox);
     rechts iets minder ruimte dan links. De vaste hoogte houdt het veld precies even hoog als de
     titel- en keuzevelden, ook naast een keuzelijst in FieldPair (rustig uitgelijnd). */
  box-sizing: border-box;
  block-size: var(--touch-min);
  padding: 8px var(--space-2) 8px var(--space-3);
}

/* Chromium en WebKit: geen extra binnenruimte rond de cijfers, zodat niets wordt afgekapt. */
.input::-webkit-datetime-edit,
.input::-webkit-datetime-edit-fields-wrapper {
  padding: 0;
}

/* Het kalender-/klokicoon staat naast de tekst, met een ruimer tikvlak. De negatieve marge boven
   en onder heft de extra binnenruimte op, zodat het icoon het veld niet hoger maakt (Chromium
   telde die 2 × 6 px anders mee: 51/54 px in plaats van 44 px). Vaste icoonmaat: Edge tekent de
   klok anders groter (20 px) dan de kalender (14 px), en dan lopen Datum en Van/Tot ongelijk. */
.input::-webkit-calendar-picker-indicator {
  flex: none;
  inline-size: 1rem;
  block-size: 1rem;
  margin: -6px 0 -6px var(--space-2);
  padding: 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  opacity: 0.7;
}

.input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
  background-color: var(--surface-hover);
}

/* iOS Safari: waarde links uitlijnen (standaard gecentreerd) en een leeg veld niet laten
   inklappen. */
.input::-webkit-date-and-time-value {
  margin: 0;
  min-height: 1.25em;
  text-align: start;
}

/* Donker thema: het native icoon en de kiezer ook donker. */
:global([data-theme='dark']) .input,
:global([data-theme='night']) .input {
  color-scheme: dark;
}

/* ---- Tijdveld (TimeField.tsx) ---- */

/* Label links, de duur ("1 uur") rechts op dezelfde regel: nooit in het veld, dus nooit afgekapt. */
.labelRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-width: 0;
  margin-bottom: 6px;
}

.labelRow .label {
  margin-bottom: 0;
}

.labelRowHidden {
  margin-bottom: 0;
}

/* line-height 1 en negatieve marge: de badge maakt de labelregel niet hoger, zodat Van en Tot
   precies op één lijn blijven. */
.durationBadge {
  flex: none;
  margin: -3px 0;
  padding: 4px var(--space-2);
  line-height: 1;
  border-radius: var(--radius-pill);
  background: var(--surface-sunken);
  color: var(--text-muted);
  font-size: var(--text-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.control {
  position: relative;
}

/* De tijd groot en duidelijk; rechts ruimte voor de klokknop (die ligt binnen de rand). Tekst in
   een tekstveld staat vanzelf verticaal in het midden, dus geen verticale padding. */
.input.timeInput {
  padding: 0 calc(var(--touch-min) + 4px) 0 var(--space-3);
  font-size: var(--b-text-title-3);
  font-weight: 600;
}

.input.timeInput::placeholder {
  font-weight: 400;
}

.clock {
  position: absolute;
  top: 0;
  inset-inline-end: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--touch-min);
  height: var(--touch-min);
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

@media (hover: hover) {
  .clock:hover:not(:disabled) {
    background: var(--surface-hover);
    color: var(--text);
  }

  .chip:hover:not(:disabled) {
    border-color: var(--accent);
  }
}

.clock:focus-visible {
  outline: 3px solid var(--focus-ring);
  outline-offset: -3px;
}

/* Terug na een keuze met de vinger: focus wel (schermlezer), maar geen dikke ring. */
.clock[data-quiet-focus]:focus-visible {
  outline: none;
}

.clock:disabled {
  cursor: default;
  opacity: 0.5;
}

.clock[aria-expanded='true'] {
  color: var(--accent);
}

/* Duurknoppen onder Van/Tot: vijf naast elkaar, ook op 390 px (twee regels: duur en eindtijd). */
.chips {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
  margin-top: var(--space-3);
}

.chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  /* Twee regels (duur en eindtijd): de grote knophoogte. */
  min-height: var(--b-control-large);
  padding: 4px 2px;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--text);
  font: inherit;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out);
}

.chip:focus-visible {
  outline: 3px solid var(--focus-ring);
  outline-offset: 2px;
}

.chip:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.chipOn {
  border-color: var(--accent);
  background: var(--accent-tint);
}

.chipText {
  font-size: var(--text-sm);
  font-weight: 600;
  white-space: nowrap;
}

.chipEnd {
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
}

.chipOn .chipEnd {
  color: var(--text);
}

/* Velden naast elkaar in gelijke kolommen; past het niet, dan onder elkaar. Bovenkanten gelijk
   (start, niet end): alle velden zijn even hoog (44 px), dus de onderkanten lijnen dan ook uit,
   en een foutmelding of hint onder één veld duwt het andere veld niet omhoog. */
.pair {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--pair-min, 140px)), 1fr));
  gap: var(--space-3);
  align-items: start;
}

.pair.pair > * {
  min-width: 0;
  margin-bottom: 0;
}
